import { useEffect, useRef } from 'react'

import { cn } from '@/lib/utils'
import type { ChatMessage } from '@/lib/api/support-chat-types'

import { AssistantContent } from './assistant-content'
import { ToolCallCard } from './tool-call-card'

interface QuickStart {
  label: string
  prefill: string
}

const QUICK_STARTS: QuickStart[] = [
  { label: 'Look up an attendee', prefill: 'Look up an attendee — phone, email, or user ID: ' },
  { label: 'Top selling items', prefill: 'What were the top selling items?' },
  { label: 'Top vendors by revenue', prefill: 'Which vendors brought in the most revenue?' },
  { label: 'Refund an order', prefill: 'I need to refund order #' },
  { label: 'Payment declined', prefill: "Attendee's card keeps getting declined — email: " },
  { label: 'Order not redeemed', prefill: 'Vendor delivered but order shows open — order #' },
]

interface ChatMessageListProps {
  messages: ChatMessage[]
  isStreaming: boolean
  onConfirmAction?: (pendingActionId: number) => Promise<void>
  onCancelAction?: (pendingActionId: number) => Promise<void>
  onQuickStart?: (prefill: string) => void
}

export function ChatMessageList({
  messages,
  isStreaming,
  onConfirmAction,
  onCancelAction,
  onQuickStart,
}: ChatMessageListProps) {
  const bottomRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
  }, [messages, isStreaming])

  if (messages.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 px-6 py-6 text-center text-sm text-neutral-500 dark:text-neutral-400">
        <div>
          <p className="font-medium text-neutral-700 dark:text-neutral-200">Ask me anything</p>
          <p className="mt-1 max-w-xs">
            Look up attendees, vendors, and orders, check top sellers and revenue, or take an
            action like a refund. Add a phone, email, or user ID when it&apos;s about someone.
          </p>
        </div>
        {onQuickStart && (
          <div className="flex w-full max-w-xs flex-wrap justify-center gap-1.5">
            {QUICK_STARTS.map((qs) => (
              <button
                key={qs.label}
                type="button"
                onClick={() => onQuickStart(qs.prefill)}
                className="rounded-full border border-neutral-200 bg-white px-2.5 py-1 text-[11px] font-medium text-neutral-700 transition-colors hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-200 dark:hover:border-blue-900 dark:hover:bg-blue-950/40 dark:hover:text-blue-300"
              >
                {qs.label}
              </button>
            ))}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="flex flex-1 flex-col gap-3 overflow-y-auto px-4 py-4">
      {messages.map((msg) => (
        <MessageBubble
          key={msg.id}
          message={msg}
          onConfirmAction={onConfirmAction}
          onCancelAction={onCancelAction}
        />
      ))}
      {isStreaming && (
        <div className="text-xs text-neutral-500 dark:text-neutral-400">Assistant is thinking…</div>
      )}
      <div ref={bottomRef} />
    </div>
  )
}

function MessageBubble({
  message,
  onConfirmAction,
  onCancelAction,
}: {
  message: ChatMessage
  onConfirmAction?: (pendingActionId: number) => Promise<void>
  onCancelAction?: (pendingActionId: number) => Promise<void>
}) {
  const isUser = message.role === 'user'
  const toolCalls = message.tool_calls ?? []

  if (isUser) {
    return (
      <div className="max-w-[85%] self-end rounded-lg bg-blue-600 px-3 py-2 text-sm leading-relaxed text-white">
        {message.content}
      </div>
    )
  }

  return (
    <div className="flex w-full flex-col gap-2">
      {message.content && (
        <div
          className={cn(
            'max-w-[85%] self-start rounded-lg bg-neutral-100 px-3 py-2 leading-relaxed text-neutral-900',
            'dark:bg-neutral-800 dark:text-neutral-100',
          )}
        >
          <AssistantContent content={message.content} />
        </div>
      )}
      {toolCalls.length > 0 && (
        <div className="flex max-w-full flex-col gap-1.5">
          {toolCalls.map((tc) => (
            <ToolCallCard
              key={tc.id}
              toolCall={tc}
              onConfirm={onConfirmAction}
              onCancel={onCancelAction}
            />
          ))}
        </div>
      )}
      {!message.content && toolCalls.length === 0 && (
        <div className="max-w-[85%] self-start rounded-lg bg-neutral-100 px-3 py-2 text-sm leading-relaxed text-neutral-400 dark:bg-neutral-800">
          …
        </div>
      )}
    </div>
  )
}
